

const DBG = () => !!window.ANI_LIQUID_DEBUG;
const dlog = (...args) => {
  if (DBG()) console.log('[ani-liquid-dominant]', ...args);
};


function parsePaletteRaw(raw) {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
}

function resolveColorToken(token, contextEl) {
  const root = contextEl?.ownerDocument?.documentElement || document.documentElement;
  const styles = getComputedStyle(root);

  
  const varMatch = token.match(/^var\((--[a-zA-Z0-9_-]+)\)$/);
  if (varMatch) {
    const varName = varMatch[1];
    const value = styles.getPropertyValue(varName).trim();
    if (value) return value;
    return token;
  }

  
  if (token.startsWith('--')) {
    const rgb = styles.getPropertyValue(token).trim();
    if (rgb) {
      return rgb.startsWith('rgb') ? rgb : `rgb(${rgb})`;
    }
    return token;
  }

  
  const varName = `--${token}-rgb`;
  const rgb = styles.getPropertyValue(varName).trim();
  if (rgb) {
    return rgb.startsWith('rgb') ? rgb : `rgb(${rgb})`;
  }

  
  return token;
}

function buildPalette(paletteInput, contextEl) {
  const rawTokens = Array.isArray(paletteInput)
    ? paletteInput
    : parsePaletteRaw(paletteInput);

  const resolved = rawTokens
    .map((t) => resolveColorToken(t, contextEl))
    .filter(Boolean);

  if (!resolved.length) {
    
    return ['rgb(15 23 42)', 'rgb(15 23 42)'];
  }
  return resolved;
}

function resizeCanvasToDisplaySize(canvas) {
  const rect = canvas.getBoundingClientRect();
  const width = rect.width || 0;
  const height = rect.height || 0;
  const dpr = window.devicePixelRatio || 1;

  const displayWidth = Math.round(width * dpr);
  const displayHeight = Math.round(height * dpr);

  if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
    canvas.width = displayWidth;
    canvas.height = displayHeight;
    return true;
  }
  return false;
}


const LIQUID_ENGINES = new WeakMap();

function createEngine(canvas, config) {
  const engine = {
    canvas,
    config: { ...config },
    blobs: [],
    pointer: {
      x: 0.5,
      y: 0.5,
      active: false,
      strength: 0,
      lastMoveTs: 0
    },
    lastT: 0,
    autoPhase: 0
  };

  const numBlobs = Math.max(3, Math.min(8, engine.config.palette.length + 3));
  for (let i = 0; i < numBlobs; i++) {
    engine.blobs.push({
      angle: Math.random() * Math.PI * 2,
      radius: 0.25 + Math.random() * 0.3,
      speed: 0.4 + Math.random() * 0.6,
      offset: Math.random() * 1000,
      colorIndex: i % engine.config.palette.length
    });
  }

  const onPointerMove = (ev) => {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = (ev.clientX - rect.left) / rect.width;
    const y = (ev.clientY - rect.top) / rect.height;

    engine.pointer.x = x;
    engine.pointer.y = y;
    engine.pointer.active = true;
    engine.pointer.lastMoveTs = performance.now();
  };

  const onPointerLeave = () => {
    engine.pointer.active = false;
  };

  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerdown', onPointerMove);
  canvas.addEventListener('pointerleave', onPointerLeave);

  engine.cleanup = () => {
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerdown', onPointerMove);
    canvas.removeEventListener('pointerleave', onPointerLeave);
  };

  return engine;
}

function getEngine(canvas, config) {
  let engine = LIQUID_ENGINES.get(canvas);
  if (!engine) {
    engine = createEngine(canvas, config);
    LIQUID_ENGINES.set(canvas, engine);
  } else {
    engine.config = { ...engine.config, ...config };
  }
  return engine;
}


function createLiquidEtherInstance(canvas, options = {}) {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    dlog('kein 2D-Kontext', canvas);
    return {
      setActive() {},
      destroy() {}
    };
  }

  const palette = buildPalette(options.palette || options.colors, options.contextEl);
  const intensity =
    typeof options.intensity === 'number' && !Number.isNaN(options.intensity)
      ? options.intensity
      : 1;

  const config = {
    palette,
    intensity,
    mouseForce:
      typeof options.mouseForce === 'number' && !Number.isNaN(options.mouseForce)
        ? options.mouseForce
        : 20,
    cursorSize:
      typeof options.cursorSize === 'number' && !Number.isNaN(options.cursorSize)
        ? options.cursorSize
        : 120,
    autoDemo: options.autoDemo !== false,
    autoSpeed:
      typeof options.autoSpeed === 'number' && !Number.isNaN(options.autoSpeed)
        ? options.autoSpeed
        : 0.5,
    autoIntensity:
      typeof options.autoIntensity === 'number' && !Number.isNaN(options.autoIntensity)
        ? options.autoIntensity
        : 2.2,
    autoResumeDelay:
      typeof options.autoResumeDelay === 'number' && !Number.isNaN(options.autoResumeDelay)
        ? options.autoResumeDelay
        : 3000,
    takeoverDuration:
      typeof options.takeoverDuration === 'number' && !Number.isNaN(options.takeoverDuration)
        ? options.takeoverDuration
        : 0.25,
    autoRampDuration:
      typeof options.autoRampDuration === 'number' && !Number.isNaN(options.autoRampDuration)
        ? options.autoRampDuration
        : 0.6
  };

  const respectReduced = options.respectReduced !== false;
  const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  let reduce = respectReduced && mql.matches;

  let active = false;
  let frameId = null;
  let startTime = performance.now();

  function onMediaChange(e) {
    reduce = respectReduced && e.matches;
    if (reduce && active) {
      active = false;
      dlog('deaktiviert wegen prefers-reduced-motion');
    }
  }

  if (mql.addEventListener) {
    mql.addEventListener('change', onMediaChange);
  } else if (mql.addListener) {
    mql.addListener(onMediaChange);
  }

  function loop(now) {
    if (!active) {
      frameId = null;
      return;
    }

    const t = (now - startTime) * 0.001;
    resizeCanvasToDisplaySize(canvas);
    const width = canvas.width;
    const height = canvas.height;
    if (!width || !height) {
      frameId = requestAnimationFrame(loop);
      return;
    }

    ctx.clearRect(0, 0, width, height);

    renderLiquidFrame(ctx, width, height, t, config);

    frameId = requestAnimationFrame(loop);
  }

  resizeCanvasToDisplaySize(canvas);

  return {
    setActive(on) {
      if (reduce && on) {
        dlog('Aktivierung ignoriert (prefers-reduced-motion: reduce)');
        return;
      }
      const next = !!on;
      if (next === active) return;

      active = next;
      if (active && frameId == null) {
        startTime = performance.now();
        frameId = requestAnimationFrame(loop);
      }
    },
    destroy() {
      active = false;
      if (frameId != null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }

      if (mql.removeEventListener) {
        mql.removeEventListener('change', onMediaChange);
      } else if (mql.removeListener) {
        mql.removeListener(onMediaChange);
      }

      const engine = LIQUID_ENGINES.get(canvas);
      if (engine && engine.cleanup) {
        try {
          engine.cleanup();
        } catch (e) {
          console.error(e);
        }
      }
      LIQUID_ENGINES.delete(canvas);
    }
  };
}


export function initLiquidEtherDominant(options = {}) {
  const selector = options.selector || '[data-liquid-ether][data-ani-section]';
  const sections = Array.from(document.querySelectorAll(selector));

  if (!sections.length) {
    dlog('keine Liquid-Dominant-Sektionen gefunden', selector);
    return () => {};
  }

  const instances = new Map();

  function ensureInstance(section) {
    if (instances.has(section)) return instances.get(section);

    const host = section.querySelector('.liquid-ether-bg');
    if (!host) {
      dlog('Sektion ohne .liquid-ether-bg', section);
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'liquid-ether-canvas';
    host.appendChild(canvas);

    const paletteAttr = section.getAttribute('data-le-colors');
    const speedAttr = section.getAttribute('data-le-speed'); 
    const intensityAttr = section.getAttribute('data-le-intensity');

    const mouseForceAttr = section.getAttribute('data-le-mouse-force');
    const cursorSizeAttr = section.getAttribute('data-le-cursor-size');
    const autoDemoAttr = section.getAttribute('data-le-auto-demo');
    const autoSpeedAttr = section.getAttribute('data-le-auto-speed');
    const autoIntensityAttr = section.getAttribute('data-le-auto-intensity');
    const autoResumeDelayAttr = section.getAttribute('data-le-auto-resume-delay');
    const takeoverDurationAttr = section.getAttribute('data-le-takeover-duration');
    const autoRampDurationAttr = section.getAttribute('data-le-auto-ramp-duration');

    const speed = speedAttr != null && speedAttr !== '' ? parseFloat(speedAttr) : undefined;
    const intensity =
      intensityAttr != null && intensityAttr !== '' ? parseFloat(intensityAttr) : undefined;

    const mouseForce =
      mouseForceAttr != null && mouseForceAttr !== '' ? parseFloat(mouseForceAttr) : undefined;
    const cursorSize =
      cursorSizeAttr != null && cursorSizeAttr !== '' ? parseFloat(cursorSizeAttr) : undefined;
    const autoSpeed =
      autoSpeedAttr != null && autoSpeedAttr !== '' ? parseFloat(autoSpeedAttr) : undefined;
    const autoIntensity =
      autoIntensityAttr != null && autoIntensityAttr !== ''
        ? parseFloat(autoIntensityAttr)
        : undefined;
    const autoResumeDelay =
      autoResumeDelayAttr != null && autoResumeDelayAttr !== ''
        ? parseFloat(autoResumeDelayAttr)
        : undefined;
    const takeoverDuration =
      takeoverDurationAttr != null && takeoverDurationAttr !== ''
        ? parseFloat(takeoverDurationAttr)
        : undefined;
    const autoRampDuration =
      autoRampDurationAttr != null && autoRampDurationAttr !== ''
        ? parseFloat(autoRampDurationAttr)
        : undefined;

    const autoDemo =
      autoDemoAttr == null
        ? undefined
        : !(autoDemoAttr === 'false' || autoDemoAttr === '0' || autoDemoAttr === 'off');

    const inst = createLiquidEtherInstance(canvas, {
      palette: paletteAttr,
      speed: Number.isFinite(speed) ? speed : undefined,
      intensity: Number.isFinite(intensity) ? intensity : undefined,
      mouseForce: Number.isFinite(mouseForce) ? mouseForce : undefined,
      cursorSize: Number.isFinite(cursorSize) ? cursorSize : undefined,
      autoDemo,
      autoSpeed: Number.isFinite(autoSpeed) ? autoSpeed : undefined,
      autoIntensity: Number.isFinite(autoIntensity) ? autoIntensity : undefined,
      autoResumeDelay: Number.isFinite(autoResumeDelay) ? autoResumeDelay : undefined,
      takeoverDuration: Number.isFinite(takeoverDuration) ? takeoverDuration : undefined,
      autoRampDuration: Number.isFinite(autoRampDuration) ? autoRampDuration : undefined,
      respectReduced: options.respectReduced,
      contextEl: section
    });

    instances.set(section, inst);
    return inst;
  }

  function updateActiveFromClasses() {
    const dominant =
      sections.find((sec) => sec.classList.contains('is-dominant')) || null;

    sections.forEach((sec) => {
      const inst = ensureInstance(sec);
      if (!inst) return;
      const isActive = sec === dominant;
      inst.setActive(isActive);
      sec.classList.toggle('is-liquid-dominant', isActive);
    });
  }

  
  updateActiveFromClasses();

  const observer = new MutationObserver((mutations) => {
    let dirty = false;
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'class') {
        const el = m.target;
        if (
          el instanceof Element &&
          el.matches('[data-ani-section][data-liquid-ether]')
        ) {
          dirty = true;
        }
      }
    }
    if (dirty) updateActiveFromClasses();
  });

  sections.forEach((sec) => {
    observer.observe(sec, { attributes: true, attributeFilter: ['class'] });
  });

  return () => {
    observer.disconnect();
    instances.forEach((inst) => {
      try {
        inst.destroy();
      } catch (e) {
        console.error(e);
      }
    });
    instances.clear();
  };
}


export function renderLiquidFrame(ctx, width, height, t, config) {
  const canvas = ctx.canvas;
  const engine = getEngine(canvas, config);

  const {
    palette,
    intensity,
    mouseForce,
    cursorSize,
    autoDemo,
    autoSpeed,
    autoIntensity,
    autoResumeDelay,
    takeoverDuration,
    autoRampDuration
  } = engine.config;

  const now = performance.now();
  const dt = engine.lastT ? Math.max(0.001, t - engine.lastT) : 0.016;
  engine.lastT = t;

  const pointer = engine.pointer;
  const timeSinceMove = now - (pointer.lastMoveTs || 0);

  
  let pointerFactor = 0;
  if (pointer.active) {
    pointerFactor = 1;
  } else if (timeSinceMove < autoResumeDelay) {
    const k = Math.min(1, timeSinceMove / (takeoverDuration * 1000));
    pointerFactor = 1 - k;
  } else {
    pointerFactor = 0;
  }

  
  if (autoDemo && pointerFactor < 1) {
    engine.autoPhase += dt * autoSpeed;
  }

  const cx = width / 2;
  const cy = height / 2;
  const baseRadius = Math.sqrt(width * width + height * height) * 0.4 * intensity;

  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.fillRect(0, 0, width, height);

  ctx.globalCompositeOperation = 'lighter';

  const numBlobs = engine.blobs.length;

  for (let i = 0; i < numBlobs; i++) {
    const blob = engine.blobs[i];
    const color = palette[blob.colorIndex % palette.length];

    
    const autoAngle = blob.angle + engine.autoPhase * blob.speed;
    const autoRadius = blob.radius;

    const autoX = cx + Math.cos(autoAngle) * width * autoRadius;
    const autoY = cy + Math.sin(autoAngle * 1.3) * height * autoRadius;

    let x = autoX;
    let y = autoY;

    
    if (pointerFactor > 0) {
      const px = pointer.x * width;
      const py = pointer.y * height;

      const dx = px - autoX;
      const dy = py - autoY;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;

      const maxDist = cursorSize;
      const norm = Math.max(0, 1 - dist / maxDist);

      const strength = mouseForce * norm * pointerFactor;
      x += (dx / dist) * strength;
      y += (dy / dist) * strength;
    }

    const radiusBase = baseRadius * (0.4 + 0.4 * (i / numBlobs));
    const pulse = 1 + 0.25 * Math.sin(t * autoIntensity + blob.offset);
    const r = radiusBase * pulse;

    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, color);
    grad.addColorStop(0.5, color);
    grad.addColorStop(1, 'rgba(0,0,0,0)');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalCompositeOperation = 'source-over';
}
